import { useQuery } from '@tanstack/react-query';
import { serverCheckApi } from '../../api/serverCheck.js';
import { Spinner, ErrorBox } from '../../components/ui.js';

/**
 * 서버체크 > Redis 상황체크 (2026-09-05, Redis 를 새로 설치하며 요청).
 *
 * 조회 하나만 있는 화면이라 정의 기반 리포트(ReportBody)를 쓰지 않고, 다른
 * 상태 대시보드류(xadm/AdwordsPage.tsx 등)처럼 직접 짠다. 서버(redis.service.ts)
 * 가 INFO 를 이미 필요한 값만 추려서 내려주므로, 여기서는 그대로 카드에
 * 옮겨 담기만 한다.
 *
 * 10초마다 자동 갱신한다 - "지금 살아있는지"를 보려고 여는 화면이라 매번
 * 수동으로 새로고침하게 두면 불편하다. 다만 배송현황 카운터(키오스크)처럼
 * 초 단위로 튀는 화면은 아니라서 폴링 주기는 그보다 넉넉하게 뒀다.
 */
const REFRESH_MS = 10_000;

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}일 ${h}시간`;
  if (h > 0) return `${h}시간 ${m}분`;
  return `${m}분`;
}

function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / 1024 ** i).toFixed(i === 0 ? 0 : 1)}${units[i]}`;
}

/** rdb_last_save_time 은 unix seconds(KST 변환 없이 그대로 온다) - 한국 시간으로 보여준다. */
function formatUnixSeconds(seconds: number): string {
  if (!seconds) return '-';
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Asia/Seoul',
    dateStyle: 'short',
    timeStyle: 'medium',
  }).format(new Date(seconds * 1000));
}

export function RedisStatusPage() {
  const status = useQuery({
    queryKey: ['server-check', 'redis'],
    queryFn: () => serverCheckApi.redisStatus(),
    refetchInterval: REFRESH_MS,
  });

  return (
    <section>
      <header className="page-head">
        <div>
          <h1 className="page-title">
            Redis 상황체크
            {status.data && (
              <span className={status.data.connected ? 'badge badge--ok' : 'badge badge--danger'}>
                {status.data.connected ? '연결됨' : '연결 안 됨'}
              </span>
            )}
          </h1>
          <p className="hint" style={{ margin: '4px 0 0' }}>
            {REFRESH_MS / 1000}초마다 자동으로 다시 확인합니다.
            {status.dataUpdatedAt > 0 &&
              ` 최근 확인 ${formatUnixSeconds(Math.floor(status.dataUpdatedAt / 1000))}`}
          </p>
        </div>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => void status.refetch()}
          disabled={status.isFetching}
        >
          {status.isFetching ? '확인 중…' : '지금 확인'}
        </button>
      </header>

      {status.isLoading && <Spinner label="Redis 상태를 확인하는 중…" />}
      {status.isError && <ErrorBox error={status.error} onRetry={() => status.refetch()} />}

      {status.data && !status.data.connected && (
        <div className="state state--error" role="alert">
          <strong>Redis 에 연결할 수 없습니다</strong>
          <span>{status.data.error}</span>
          <button type="button" className="btn btn--ghost" onClick={() => void status.refetch()}>
            다시 시도
          </button>
        </div>
      )}

      {status.data?.connected && (
        <>
          <div className="report-exp-summary">
            <div className="report-exp-summary__item report-exp-summary__item--primary">
              <span>PING 지연시간</span>
              <strong>{status.data.latencyMs}ms</strong>
              <small>왕복 시간</small>
            </div>
            <div className="report-exp-summary__item">
              <span>연결된 클라이언트</span>
              <strong>{status.data.clients?.connected}</strong>
              <small>최대 {status.data.clients?.maxClients.toLocaleString()}</small>
            </div>
            <div className="report-exp-summary__item">
              <span>사용 메모리</span>
              <strong>{status.data.memory?.usedHuman}</strong>
              <small>피크 {status.data.memory?.peakHuman}</small>
            </div>
            <div className="report-exp-summary__item">
              <span>초당 명령 처리</span>
              <strong>{status.data.stats?.opsPerSec.toLocaleString()}</strong>
              <small>ops/sec</small>
            </div>
            <div
              className={
                status.data.stats?.hitRatePct != null && status.data.stats.hitRatePct < 80
                  ? 'report-exp-summary__item report-exp-summary__item--attention'
                  : 'report-exp-summary__item report-exp-summary__item--winner'
              }
            >
              <span>캐시 적중률</span>
              <strong>
                {status.data.stats?.hitRatePct == null ? '-' : `${status.data.stats.hitRatePct.toFixed(1)}%`}
              </strong>
              <small>
                히트 {status.data.stats?.keyspaceHits.toLocaleString()} · 미스{' '}
                {status.data.stats?.keyspaceMisses.toLocaleString()}
              </small>
            </div>
            <div className="report-exp-summary__item">
              <span>가동 시간</span>
              <strong>{status.data.server && formatUptime(status.data.server.uptimeSeconds)}</strong>
              <small>v{status.data.server?.version}</small>
            </div>
          </div>

          <div className="detail-grid">
            <div className="card">
              <h2 className="card__title">서버</h2>
              <dl className="deflist">
                <dt>버전</dt>
                <dd>{status.data.server?.version}</dd>
                <dt>모드</dt>
                <dd>{status.data.server?.mode}</dd>
                <dt>OS</dt>
                <dd>{status.data.server?.os}</dd>
                <dt>포트</dt>
                <dd>{status.data.server?.tcpPort}</dd>
              </dl>
            </div>

            <div className="card">
              <h2 className="card__title">메모리</h2>
              <dl className="deflist">
                <dt>사용 중</dt>
                <dd>
                  {status.data.memory?.usedHuman}
                  <span className="td-muted"> ({formatBytes(status.data.memory?.usedBytes ?? 0)})</span>
                </dd>
                <dt>최대(maxmemory)</dt>
                <dd>{status.data.memory?.maxBytes ? formatBytes(status.data.memory.maxBytes) : '제한 없음'}</dd>
                <dt>정책</dt>
                <dd>{status.data.memory?.maxmemoryPolicy}</dd>
                <dt>파편화율</dt>
                <dd>{status.data.memory?.fragmentationRatio.toFixed(2)}</dd>
              </dl>
            </div>

            <div className="card">
              <h2 className="card__title">지속성(저장)</h2>
              <dl className="deflist">
                <dt>RDB 마지막 저장</dt>
                <dd>{status.data.persistence && formatUnixSeconds(status.data.persistence.rdbLastSaveTime)}</dd>
                <dt>RDB 상태</dt>
                <dd>{status.data.persistence?.rdbLastBgsaveStatus}</dd>
                <dt>AOF</dt>
                <dd>{status.data.persistence?.aofEnabled ? '켜짐' : '꺼짐'}</dd>
              </dl>
            </div>

            <div className="card">
              <h2 className="card__title">복제</h2>
              <dl className="deflist">
                <dt>역할</dt>
                <dd>{status.data.replication?.role}</dd>
                <dt>연결된 슬레이브</dt>
                <dd>{status.data.replication?.connectedSlaves}</dd>
              </dl>
            </div>
          </div>

          <div className="panel">
            <div className="panel__head">
              <h2 className="panel__title">키 스페이스</h2>
              <span className="panel__count">DB {status.data.keyspace.length}개</span>
            </div>
            {status.data.keyspace.length === 0 ? (
              <p className="hint">저장된 키가 없습니다.</p>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>DB</th>
                      <th style={{ textAlign: 'right' }}>키 개수</th>
                      <th style={{ textAlign: 'right' }}>만료 설정된 키</th>
                    </tr>
                  </thead>
                  <tbody>
                    {status.data.keyspace.map((db) => (
                      <tr key={db.db}>
                        <td>{db.db}</td>
                        <td style={{ textAlign: 'right' }}>{db.keys.toLocaleString()}</td>
                        <td style={{ textAlign: 'right' }}>{db.expires.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}
